import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Building, Mail, Phone, MapPin, User, CreditCard, Package, Users, Edit, Settings } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ClientDetailsPanelProps {
  clientId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEditClient: () => void;
  onManageModules: () => void;
  onManageUsers: () => void;
}

export const ClientDetailsPanel = ({
  clientId,
  open,
  onOpenChange,
  onEditClient,
  onManageModules,
  onManageUsers,
}: ClientDetailsPanelProps) => {
  const { data: client, isLoading: isLoadingClient } = useQuery({
    queryKey: ['client-details', clientId],
    queryFn: async () => {
      if (!clientId) return null;
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', clientId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!clientId && open,
  });

  const { data: userCount = 0 } = useQuery({
    queryKey: ['client-user-count', clientId],
    queryFn: async () => {
      if (!clientId) return 0;
      const { data, error } = await supabase
        .from('users')
        .select('id, client_id, client_ids')
        .eq('status', 'active');
      
      if (error) throw error;
      
      return data?.filter(user => {
        if (user.client_id === clientId) return true;
        if (user.client_ids && Array.isArray(user.client_ids)) {
          return user.client_ids.includes(clientId);
        }
        return false;
      }).length || 0;
    },
    enabled: !!clientId && open,
  });

  const { data: modules = [] } = useQuery({
    queryKey: ['client-modules-details', clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('client_modules')
        .select('*')
        .eq('client_id', clientId)
        .eq('is_active', true);
      if (error) throw error;
      return data;
    },
    enabled: !!clientId && open,
  });

  const { data: license } = useQuery({
    queryKey: ['client-license-details', clientId],
    queryFn: async () => {
      if (!clientId) return null;
      const { data, error } = await supabase
        .from('client_licenses')
        .select('*, license_plans(*)')
        .eq('client_id', clientId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!clientId && open,
  });

  if (!open) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[500px] sm:max-w-[500px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Detalhes do Cliente
          </SheetTitle>
          <SheetDescription>
            Informações completas do cliente
          </SheetDescription>
        </SheetHeader>

        {isLoadingClient ? (
          <div className="space-y-4 mt-6">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : client ? (
          <div className="mt-6 space-y-6">
            {/* Header with status */}
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-semibold">{client.name}</h3>
                <p className="text-sm text-muted-foreground">{client.nome_fantasia || client.name}</p>
              </div>
              <Badge variant={client.status === 'active' ? 'default' : 'secondary'}>
                {client.status === 'active' ? 'Ativo' : 'Inativo'}
              </Badge>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onEditClient}>
                <Edit className="h-4 w-4 mr-1" />
                Editar
              </Button>
              <Button variant="outline" size="sm" onClick={onManageModules}>
                <Package className="h-4 w-4 mr-1" />
                Módulos
              </Button>
              <Button variant="outline" size="sm" onClick={onManageUsers}>
                <Users className="h-4 w-4 mr-1" />
                Usuários
              </Button>
            </div>

            <Separator />

            {/* Contact Info */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Contato</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{client.email || 'Não informado'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{client.phone || 'Não informado'}</span>
                </div>
                {client.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span>
                      {client.address}
                      {client.cidade && `, ${client.cidade}`}
                      {client.estado && ` - ${client.estado}`}
                      {client.cep && ` (${client.cep})`}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Business Info */}
            {(client.cnpj || client.razao_social) && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Dados Empresariais</h4>
                  <div className="space-y-2 text-sm">
                    {client.cnpj && <p><span className="text-muted-foreground">CNPJ:</span> {client.cnpj}</p>}
                    {client.razao_social && <p><span className="text-muted-foreground">Razão Social:</span> {client.razao_social}</p>}
                  </div>
                </div>
              </>
            )}

            {/* Responsible */}
            {client.responsavel_nome && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Responsável</h4>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{client.responsavel_nome}</p>
                      <p className="text-sm text-muted-foreground">
                        {client.responsavel_cargo || 'Responsável'}
                        {client.responsavel_telefone && ` • ${client.responsavel_telefone}`}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* License Info */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Licença</h4>
              {license ? (
                <div className="p-3 border rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-primary" />
                      <span className="font-medium">{license.license_plans?.name || 'Plano'}</span>
                    </div>
                    <Badge variant={license.status === 'active' ? 'default' : 'destructive'}>
                      {license.status === 'active' ? 'Ativa' : license.status}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p>Usuários: {userCount}</p>
                    {license.end_date && (
                      <p>Expira em: {format(new Date(license.end_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nenhuma licença ativa</p>
              )}
            </div>

            <Separator />

            {/* Modules */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                Módulos Ativos ({modules.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {modules.length > 0 ? (
                  modules.map((module) => (
                    <Badge key={module.id} variant="outline">
                      {module.module_code}
                    </Badge>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">Nenhum módulo ativo</p>
                )}
              </div>
            </div>

            <Separator />

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 border rounded-lg text-center">
                <p className="text-2xl font-bold text-primary">{userCount}</p>
                <p className="text-sm text-muted-foreground">Usuários Ativos</p>
              </div>
              <div className="p-3 border rounded-lg text-center">
                <p className="text-2xl font-bold text-primary">{modules.length}</p>
                <p className="text-sm text-muted-foreground">Módulos Ativos</p>
              </div>
            </div>

            {/* Created/Updated */}
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Criado em: {format(new Date(client.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
              {client.updated_at && (
                <p>Atualizado em: {format(new Date(client.updated_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
              )}
            </div>
          </div>
        ) : (
          <p className="mt-6 text-muted-foreground">Cliente não encontrado</p>
        )}
      </SheetContent>
    </Sheet>
  );
};
